import {
  INITIAL_LUNAR_DAY,
  ORBIT_SECONDS_AT_ONE_X,
  SYNODIC_MONTH_DAYS,
} from "./constants";
import { getSimulationSnapshot, phaseIndexToDay } from "./geometry";
import { PHASE_NAMES } from "./types";

export interface SimulationGuides {
  sunlightRays: boolean;
  orbitalPlanes: boolean;
  phaseLabels: boolean;
  moonTerminator: boolean;
  moonRotationMarker: boolean;
  allPhasePositions: boolean;
  earthRotation: boolean;
  eclipseNodes: boolean;
  classroomMode: boolean;
}

export interface PhaseTransition {
  readonly startDay: number;
  readonly deltaDays: number;
  readonly elapsedSeconds: number;
  readonly durationSeconds: number;
}

export interface SimulationState {
  readonly day: number;
  readonly isPlaying: boolean;
  readonly speed: 0.25 | 0.5 | 1 | 2 | 4 | 8;
  readonly guides: SimulationGuides;
  readonly transition: PhaseTransition | null;
  readonly cameraResetToken: number;
}

export type SimulationAction =
  | { readonly type: "togglePlayback" }
  | { readonly type: "setSpeed"; readonly speed: SimulationState["speed"] }
  | { readonly type: "setDay"; readonly day: number }
  | { readonly type: "tick"; readonly elapsedSeconds: number }
  | {
      readonly type: "selectPhase";
      readonly phaseIndex: number;
      readonly direction: "shortest" | "forward" | "backward";
      readonly reduceMotion: boolean;
    }
  | { readonly type: "toggleGuide"; readonly guide: keyof SimulationGuides }
  | { readonly type: "resetView" };

const PHASE_TRANSITION_SECONDS = 0.6;
const SPEEDS = [0.25, 0.5, 1, 2, 4, 8] as const;

// Progressive disclosure: only the causal-story guides are on at first paint.
const INITIAL_GUIDES: SimulationGuides = {
  sunlightRays: true,
  orbitalPlanes: false,
  phaseLabels: false,
  moonTerminator: true,
  moonRotationMarker: true,
  allPhasePositions: false,
  earthRotation: true,
  eclipseNodes: false,
  classroomMode: false,
};

function canonicalDay(day: number): number {
  return getSimulationSnapshot(day).lunarDay;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isValidLunarDay(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0 && value <= SYNODIC_MONTH_DAYS;
}

function isValidSpeed(value: unknown): value is SimulationState["speed"] {
  return (
    isFiniteNumber(value) && SPEEDS.includes(value as (typeof SPEEDS)[number])
  );
}

function isValidPhaseIndex(value: unknown): value is number {
  return (
    isFiniteNumber(value) &&
    Number.isInteger(value) &&
    value >= 0 &&
    value < PHASE_NAMES.length
  );
}

function isValidDirection(
  value: unknown,
): value is "shortest" | "forward" | "backward" {
  return value === "shortest" || value === "forward" || value === "backward";
}

function easeInOutCubic(progress: number): number {
  if (progress < 0.5) {
    return 4 * progress * progress * progress;
  }

  return 1 - (-2 * progress + 2) ** 3 / 2;
}

function getPhaseDelta(
  startDay: number,
  targetDay: number,
  direction: "shortest" | "forward" | "backward",
): number {
  const directDelta = targetDay - startDay;

  if (direction === "forward") {
    return directDelta < 0 ? directDelta + SYNODIC_MONTH_DAYS : directDelta;
  }

  if (direction === "backward") {
    return directDelta > 0 ? directDelta - SYNODIC_MONTH_DAYS : directDelta;
  }

  const wrappedDelta =
    directDelta > 0
      ? directDelta - SYNODIC_MONTH_DAYS
      : directDelta + SYNODIC_MONTH_DAYS;
  return Math.abs(wrappedDelta) < Math.abs(directDelta)
    ? wrappedDelta
    : directDelta;
}

export function createInitialSimulationState(
  initialDay?: number | null,
): SimulationState {
  return {
    day:
      initialDay !== undefined &&
      initialDay !== null &&
      isValidLunarDay(initialDay)
        ? initialDay
        : INITIAL_LUNAR_DAY,
    isPlaying: false,
    speed: 1,
    guides: { ...INITIAL_GUIDES },
    transition: null,
    cameraResetToken: 0,
  };
}

export function simulationReducer(
  state: SimulationState,
  action: SimulationAction,
): SimulationState {
  switch (action.type) {
    case "togglePlayback":
      return {
        ...state,
        isPlaying: !state.isPlaying,
        transition: null,
      };

    case "setSpeed":
      return isValidSpeed(action.speed)
        ? { ...state, speed: action.speed }
        : state;

    case "setDay":
      return isValidLunarDay(action.day)
        ? { ...state, day: action.day, transition: null }
        : state;

    case "tick": {
      if (
        !isFiniteNumber(action.elapsedSeconds) ||
        action.elapsedSeconds <= 0
      ) {
        return state;
      }

      if (state.transition !== null) {
        const elapsedSeconds = Math.min(
          state.transition.elapsedSeconds + action.elapsedSeconds,
          state.transition.durationSeconds,
        );
        const progress = elapsedSeconds / state.transition.durationSeconds;
        const day = canonicalDay(
          state.transition.startDay +
            state.transition.deltaDays * easeInOutCubic(progress),
        );

        return {
          ...state,
          day,
          transition:
            progress === 1 ? null : { ...state.transition, elapsedSeconds },
        };
      }

      if (!state.isPlaying) {
        return state;
      }

      return {
        ...state,
        day: canonicalDay(
          state.day +
            (action.elapsedSeconds * state.speed * SYNODIC_MONTH_DAYS) /
              ORBIT_SECONDS_AT_ONE_X,
        ),
      };
    }

    case "selectPhase": {
      if (
        !isValidPhaseIndex(action.phaseIndex) ||
        !isValidDirection(action.direction)
      ) {
        return state;
      }

      const startDay = canonicalDay(state.day);
      const targetDay = phaseIndexToDay(action.phaseIndex);
      const deltaDays = getPhaseDelta(startDay, targetDay, action.direction);

      if (action.reduceMotion || deltaDays === 0) {
        return { ...state, day: targetDay, isPlaying: false, transition: null };
      }

      return {
        ...state,
        isPlaying: false,
        transition: {
          startDay,
          deltaDays,
          elapsedSeconds: 0,
          durationSeconds: PHASE_TRANSITION_SECONDS,
        },
      };
    }

    case "toggleGuide":
      return {
        ...state,
        guides: {
          ...state.guides,
          [action.guide]: !state.guides[action.guide],
        },
      };

    case "resetView":
      return { ...state, cameraResetToken: state.cameraResetToken + 1 };
  }
}
